/**
 * Customer-facing token store. Mirrors apps/web-staff/src/lib/auth/tokenStore.ts
 * but uses a separate localStorage key so a developer can be signed in to the
 * staff console and the customer portal in the same browser without
 * collisions.
 *
 * The real login form authenticates against `service-identity` at
 * `${VITE_IDENTITY_BASE_URL}/v1/auth/login`. The 401 interceptor in
 * `lib/api-client.ts` rotates the refresh token here.
 */
const STORAGE_KEY = 'upmp.customer.jwt';
const REFRESH_STORAGE_KEY = 'upmp.customer.refresh';

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function getCustomerToken(): string | null {
  const storage = safeLocalStorage();
  if (!storage) {
    return null;
  }
  const value = storage.getItem(STORAGE_KEY);
  return value && value.length > 0 ? value : null;
}

export function setCustomerToken(token: string): void {
  const storage = safeLocalStorage();
  if (!storage) {
    return;
  }
  storage.setItem(STORAGE_KEY, token);
}

export function clearCustomerToken(): void {
  const storage = safeLocalStorage();
  if (!storage) {
    return;
  }
  storage.removeItem(STORAGE_KEY);
}

export function getCustomerRefreshToken(): string | null {
  const storage = safeLocalStorage();
  if (!storage) return null;
  const value = storage.getItem(REFRESH_STORAGE_KEY);
  return value && value.length > 0 ? value : null;
}

export function setCustomerRefreshToken(token: string): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  storage.setItem(REFRESH_STORAGE_KEY, token);
}

export function clearCustomerRefreshToken(): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  storage.removeItem(REFRESH_STORAGE_KEY);
}

export const CUSTOMER_TOKEN_STORAGE_KEY = STORAGE_KEY;
export const CUSTOMER_REFRESH_TOKEN_STORAGE_KEY = REFRESH_STORAGE_KEY;
